import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Measures every media's pan against the media's own box instead of the region it is seen in.
 *
 * A media's pan (`transformMatrix` translation) used to be a fraction of its visible region: its
 * stencil mask's bounds, or the whole frame. Resizing the mask then changed how far a panned media
 * sat off centre, so cropping slid the image. The pan is now a fraction of the media's own fit box
 * (`maxWidth`/`maxHeight` times `scaleMultiplier`), so each pan is rescaled by visible region over
 * fit box and the media is drawn exactly where it was.
 */

type Size = { width: number; height: number };

const REFERENCE_RESOLUTIONS: Record<string, Size> = {
    landscape: { width: 1920, height: 1080 },
    square: { width: 1080, height: 1080 },
    vertical: { width: 1080, height: 1920 },
    fourfive: { width: 1080, height: 1350 },
};

const MEDIA_CLASS_TYPES = ['MediaAsset', 'TimeAwareMediaAsset'];
const DEFAULT_POLYGON_POINTS = [0.9, 0.1, 0.9, 0.9, 0.1, 0.9, 0.1, 0.1];
const X_TRANSLATION_INDEX = 2;
const Y_TRANSLATION_INDEX = 5;
// A visible region this close to the fit box is the same box measured another way.
const SAME_SIZE_TOLERANCE = 1e-9;

function sizeFromPoints(points: number[], resolution: Size): Size {
    const xs = points.filter((_value, index) => index % 2 === 0);
    const ys = points.filter((_value, index) => index % 2 === 1);
    return {
        width: (Math.max(...xs) - Math.min(...xs)) * resolution.width,
        height: (Math.max(...ys) - Math.min(...ys)) * resolution.height,
    };
}

/**
 * The size the renderer read off a stencil mask's bounds. Polygon bounds are combined the way the
 * renderer combines them — the larger of each extent of the start and end shapes.
 */
function maskSize(mask: Immutable.Map<string, unknown>, resolution: Size): Size {
    const classType = mask.get('classType');
    if (classType === 'PolygonMask') {
        const points = (key: string) =>
            (mask.get(key) as Immutable.List<number> | undefined)?.toArray() ??
            DEFAULT_POLYGON_POINTS;
        const start = sizeFromPoints(points('startPoints'), resolution);
        const end = sizeFromPoints(points('endPoints'), resolution);
        return {
            width: Math.max(start.width, end.width),
            height: Math.max(start.height, end.height),
        };
    }
    if (classType === 'CircleMask') {
        const radius = Math.max(
            (mask.get('startRadius') as number | undefined) ?? 0,
            (mask.get('endRadius') as number | undefined) ?? 0.5,
        );
        return { width: radius * 2, height: radius * 2 };
    }
    throw new Error(`Unknown mask type ${classType}`);
}

function migrateMedia(
    map: Immutable.Map<string, unknown>,
    assetPath: Array<string | number>,
    asset: Immutable.Map<string, unknown>,
    resolution: Size,
) {
    const matrix = asset.get('transformMatrix') as Immutable.List<number> | undefined;
    const tx = matrix?.get(X_TRANSLATION_INDEX) ?? 0;
    const ty = matrix?.get(Y_TRANSLATION_INDEX) ?? 0;
    if (tx === 0 && ty === 0) {
        return;
    }
    const mask = asset.get('mask') as Immutable.Map<string, unknown> | null | undefined;
    const visible = mask ? maskSize(mask, resolution) : resolution;
    const scaleMultiplier = (asset.get('scaleMultiplier') as number | undefined) ?? 1;
    const fitBox = {
        width:
            ((asset.get('maxWidth') as number | undefined) ?? resolution.width) * scaleMultiplier,
        height:
            ((asset.get('maxHeight') as number | undefined) ?? resolution.height) * scaleMultiplier,
    };
    const rescale = (index: number, translation: number, visibleSize: number, boxSize: number) => {
        const ratio = visibleSize / boxSize;
        if (translation === 0 || boxSize <= 0 || Math.abs(ratio - 1) < SAME_SIZE_TOLERANCE) {
            return;
        }
        map.setIn([...assetPath, 'transformMatrix', index], translation * ratio);
    };
    rescale(X_TRANSLATION_INDEX, tx, visible.width, fitBox.width);
    rescale(Y_TRANSLATION_INDEX, ty, visible.height, fitBox.height);
}

function migrateAssets(
    map: Immutable.Map<string, unknown>,
    listPath: Array<string | number>,
    resolution: () => Size,
) {
    const assets = map.getIn(listPath) as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;
    assets?.forEach((asset, index) => {
        const assetPath = [...listPath, index];
        if (asset.get('children')) {
            migrateAssets(map, [...assetPath, 'children'], resolution);
        }
        if (MEDIA_CLASS_TYPES.includes(asset.get('classType') as string)) {
            migrateMedia(map, assetPath, asset, resolution());
        }
    });
}

export const migrationTo9_248 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            map.get('slides').forEach((_scene: unknown, i: number) => {
                map.setIn(
                    ['slides', i],
                    this.migrateScene(map.getIn(['slides', i]) as Immutable.Map<string, unknown>),
                );
            });
        });
    },
    migrateScene(scene) {
        return scene.withMutations((map) => {
            const resolution = () => {
                const aspectRatio = map.get('aspectRatio') as string;
                const referenceResolution = REFERENCE_RESOLUTIONS[aspectRatio];
                if (!referenceResolution) {
                    throw new Error(`Unknown scene aspect ratio: ${aspectRatio}`);
                }
                return referenceResolution;
            };

            migrateAssets(map, ['children'], resolution);
            migrateAssets(map, ['stickers'], resolution);
            const subslides = map.get('subslides') as Immutable.List<unknown> | undefined;
            subslides?.forEach((_subslide, index) => {
                migrateAssets(map, ['subslides', index, 'children'], resolution);
            });
        });
    },
});
